/**
 * Browser Web Speech fallback (DESIGN §15): `SpeechRecognition` sends audio to the browser
 * vendor (Google/Apple) and is missing on Firefox, so it is used only when the server voice
 * service is unavailable. `speechSynthesis` speaks with a local browser voice.
 */

export type UiLanguage = "tr" | "en";

/** BCP-47 tag for a UI language. */
export const bcp47 = (lang: UiLanguage): string => (lang === "tr" ? "tr-TR" : "en-US");

/** The parts of `SpeechRecognition` the fallback uses. */
export interface RecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((ev: RecognitionResultEvent) => void) | null;
  onerror: ((ev: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
export interface RecognitionResultEvent {
  resultIndex: number;
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
}
export type RecognitionCtor = new () => RecognitionLike;

export interface SynthesisLike {
  speak(u: SpeechSynthesisUtterance): void;
  cancel(): void;
  readonly speaking: boolean;
}

export interface BrowserSpeechEnv {
  SpeechRecognition?: RecognitionCtor;
  webkitSpeechRecognition?: RecognitionCtor;
  speechSynthesis?: SynthesisLike;
  SpeechSynthesisUtterance?: typeof SpeechSynthesisUtterance;
}

export interface BrowserRecognition {
  /** Resolves with the recognized text once recognition ends. */
  result: Promise<string>;
  stop(): void;
  abort(): void;
}

export class BrowserSpeech {
  constructor(private readonly env: BrowserSpeechEnv = globalThis) {}

  private get Recognition(): RecognitionCtor | undefined {
    return this.env.SpeechRecognition ?? this.env.webkitSpeechRecognition;
  }

  get canRecognize(): boolean {
    return typeof this.Recognition === "function";
  }

  get canSynthesize(): boolean {
    return !!this.env.speechSynthesis && typeof this.env.SpeechSynthesisUtterance === "function";
  }

  /** Push-to-talk recognition: starts now, `stop()` ends it and resolves `result`. */
  recognize(lang: UiLanguage): BrowserRecognition {
    const Ctor = this.Recognition;
    if (!Ctor) {
      return {
        result: Promise.reject(new Error("speech recognition unavailable")),
        stop: () => undefined,
        abort: () => undefined,
      };
    }
    const rec = new Ctor();
    rec.lang = bcp47(lang);
    rec.continuous = true;
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    const finals: string[] = [];
    const result = new Promise<string>((resolve, reject) => {
      rec.onresult = (ev) => {
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          const r = ev.results[i]!;
          if (r.isFinal && r[0]) finals.push(r[0].transcript.trim());
        }
      };
      rec.onerror = (ev) => {
        // "no-speech" and "aborted" just end with what we have.
        if (ev.error && ev.error !== "no-speech" && ev.error !== "aborted") {
          reject(new Error(`speech recognition failed: ${ev.error}`));
        }
      };
      rec.onend = () => resolve(finals.filter(Boolean).join(" "));
    });
    result.catch(() => undefined);
    rec.start();
    return { result, stop: () => rec.stop(), abort: () => rec.abort() };
  }

  /** Speaks `text`; resolves when done (or cancelled). */
  speak(text: string, lang: UiLanguage): Promise<void> {
    const synth = this.env.speechSynthesis;
    const Utterance = this.env.SpeechSynthesisUtterance;
    if (!synth || !Utterance) return Promise.reject(new Error("speech synthesis unavailable"));
    return new Promise((resolve) => {
      const u = new Utterance(text);
      u.lang = bcp47(lang);
      u.onend = () => resolve();
      u.onerror = () => resolve();
      synth.speak(u);
    });
  }

  cancel(): void {
    this.env.speechSynthesis?.cancel();
  }
}

export type VoiceMode = "server" | "browser" | "none";

/**
 * Which input path to use: the server (voice service through the gateway) when it is up and the
 * page can capture the microphone; else the browser's recognition; else none.
 */
export function chooseMode(input: {
  serverAvailable: boolean;
  canCapture: boolean;
  canRecognize: boolean;
}): VoiceMode {
  if (input.serverAvailable && input.canCapture) return "server";
  if (input.canRecognize) return "browser";
  return "none";
}
